import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Combobox } from '@/components/ui/combobox';
import { useI18n } from '@/contexts/I18nContext';

// Mock de categorias e técnicas
const techniqueCategories = [
  {
    id: 1,
    name: 'guard_types',
    displayName: 'Guard Types',
    techniques: [
      'Closed Guard',
      'Open Guard',
      'Half Guard',
      'Butterfly Guard',
      'Worm Guard',
      'Reverse De La Riva Guard',
      '50/50 Guard',
      'Sitting Guard',
      'No-Gi Specific Guards',
    ],
  },
  {
    id: 2,
    name: 'guard_passing',
    displayName: 'Guard Passing',
    techniques: [
      'Pressure Passing',
      'Torque/Leg Drag Passing',
      'Leg Weave Passing',
      'Floating/Smash Passing',
      'Movement-Based Passing',
      'Folding Passes',
      'No-Gi Specific Passes',
    ],
  },
  {
    id: 3,
    name: 'submissions',
    displayName: 'Submissions',
    techniques: [
      'Armlocks',
      'Chokes',
      'Leg Locks',
      'Shoulder Locks',
      'Spine/Neck Cranks',
      'No-Gi Specific Submissions',
    ],
  },
  {
    id: 4,
    name: 'sweeps_takedowns',
    displayName: 'Sweeps & Takedowns',
    techniques: [
      'Guard Sweeps',
      'Half Guard Sweeps',
      'Butterfly Sweeps',
      'De La Riva Sweeps',
      'Takedowns from Standing',
      'Counter Takedowns',
      'Pulling Guard',
    ],
  },
  {
    id: 5,
    name: 'escapes_reversals',
    displayName: 'Escapes & Reversals',
    techniques: [
      'Mount Escapes',
      'Back Escapes',
      'Side Control Escapes',
      'North South Escapes',
      'Submission Escapes',
      'Turtle Escapes',
      'Half Guard Escapes',
    ],
  },
];

type TechniquesStepProps = {
  categoryIndex: number;
  setCategoryIndex: React.Dispatch<React.SetStateAction<number>>;
  selected: Record<string, string[]>;
  setSelected: React.Dispatch<React.SetStateAction<Record<string, string[]>>>;
};

export function TechniquesStep({ categoryIndex, setCategoryIndex, selected, setSelected }: TechniquesStepProps) {
  const category = techniqueCategories[categoryIndex];
  const { t } = useI18n();

  // Estado local para seleção da categoria atual
  const [localSelected, setLocalSelected] = useState<string[]>(selected[category.name] || []);

  useEffect(() => {
    setLocalSelected(selected[category.name] || []);
  }, [categoryIndex, category.name]);

  // Efeito para sincronizar o estado local com o global
  useEffect(() => {
    setSelected((old) => ({ ...old, [category.name]: localSelected }));
  }, [localSelected, category.name, setSelected]);

  // Handler ajustado: agora só atualiza o estado local
  const handleSelect = (value: string) => {
    setLocalSelected((prev) => {
      if (prev.includes(value)) {
        return prev.filter((t) => t !== value);
      } else if (prev.length < 3) {
        return [...prev, value];
      } else {
        return [...prev.slice(1), value];
      }
    });
  };

  const handleNextCategory = () => {
    // Salvar técnicas da categoria atual
    const currentCategoryTechniques = selected[category.name] || [];
    const localSelected = [...currentCategoryTechniques];
    
    setSelected(prev => ({
      ...prev,
      [category.name]: localSelected
    }));

    // Avançar para próxima categoria ou finalizar
    if (categoryIndex < techniqueCategories.length - 1) {
      setCategoryIndex(categoryIndex + 1);
    }
  };

  const handlePrevCategory = () => {
    if (categoryIndex > 0) {
      setCategoryIndex(categoryIndex - 1);
    }
  };

  // Verificar se a categoria atual tem 3 técnicas selecionadas
  const hasThreeTechniques = localSelected.length === 3;
  const isLastCategory = categoryIndex === techniqueCategories.length - 1;

  return (
    <div className="text-black">
      <h2 className="text-lg font-semibold mb-2 font-sans">{t('onboarding.techniques.title')}</h2>
      <p className="mb-4 text-black font-sans">{t('onboarding.techniques.select_techniques')}</p>
      
      {/* Progress indicator */}
      <div className="mb-4">
        <div className="flex justify-between text-xs text-[#b0b0b0] mb-2">
          <span>{t('onboarding.techniques.category', { current: categoryIndex + 1, total: techniqueCategories.length })}</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className="bg-[#D72E16] h-2 rounded-full transition-all duration-300" 
            style={{ width: `${((categoryIndex + 1) / techniqueCategories.length) * 100}%` }}
          ></div>
        </div>
      </div>

      <div className="mb-4">
        <div className="font-medium mb-1 text-black font-sans">{t(`onboarding.techniques.categories.${category.name}`)}</div>
        <div className="text-xs text-[#b0b0b0] mb-2 font-sans">{t('onboarding.techniques.max_selected', { max: localSelected.length })}</div>
        <div className="flex flex-wrap gap-2">
          {category.techniques.map((tech) => {
            const isSelected = localSelected.includes(tech);
            const isDisabled = !isSelected && localSelected.length >= 3;
            return (
              <button
                key={tech}
                type="button"
                onClick={() => handleSelect(tech)}
                className={
                  'rounded px-3 py-1 text-sm font-medium transition-colors duration-200 border font-sans ' +
                  (isSelected
                    ? 'bg-[#D72E16] text-white border-[#D72E16]'
                    : isDisabled
                    ? 'border-black text-[#b0b0b0] bg-white cursor-not-allowed'
                    : 'border-black text-black bg-white hover:bg-[#D72E16] hover:text-white cursor-pointer') +
                  ' focus:outline-none focus:ring-0'
                }
                tabIndex={0}
                aria-pressed={isSelected}
                disabled={false}
              >
                {tech}
              </button>
            );
          })}
        </div>
        
        {/* Warning message if not 3 techniques selected */}
        {localSelected.length > 0 && localSelected.length < 3 && (
          <div className="text-xs text-orange-600 mt-2 font-sans">
            💡 Tip: You can select up to 3 techniques for better recommendations
          </div>
        )}
      </div>
    </div>
  );
} 